import React from "react"
import { IconButton, Box, Tooltip } from "@mui/material"
import Icon from "@mdi/react"
import {
	mdiTrashCanOutline,
	mdiContentCopy,
	mdiChevronLeft,
	mdiChevronRight,
	mdiPencil,
	mdiSortBoolAscendingVariant,
	mdiInformation,
} from "@mdi/js"

import TextInput from "components/Forms/TextInput"
import ChangeAmountButton from "components/Buttons/ChangeAmountButton"
import StyledLockingSystemCell from "containers/LockingSystem/Matrix/StyledLockingSystemCell"
import LockingSystemItemWrapper from "./LockingSystemItemWrapper"

import formatPrice from "@secureo/common/utils/forex/formatPrice"

import { MoveDirection } from "reducers/lockingSystemReducer"
import { ComponentCharges, LockingSystemComponent } from "@secureo/common/typings/LockingSystem"

export const defaultKeyName = "Schlüssel"

const LockingSystemKey = ({
	isLoading,
	isFrozen,
	keyComponent,
	charges,
	cellIndex,
	removeKey,
	duplicateKey,
	setComponentName,
	setComponentQuantity,
	setEditComponentModalUuid,
	moveMatrixComponent,
	toggleKeyComponentSelection,
}: Props) => {
	const { name, uuid, quantity, totalGrossPriceCents } = keyComponent

	const remove = () => {
		removeKey(uuid)
	}

	const duplicate = () => {
		duplicateKey(uuid)
	}

	const onComponentNameChange = (event: any) => {
		const name = event.target.value

		setComponentName(uuid, name)
	}

	const onComponentQuantityChange = (quantity: number) => {
		setComponentQuantity(uuid, quantity)
	}

	const onToggleSelections = () => {
		toggleKeyComponentSelection(uuid)
	}

	const fallbackName = `${defaultKeyName} ${cellIndex + 1}`

	const gridColumnName = `keyComponent ${cellIndex + 1}`

	return (
		<>
			<StyledLockingSystemCell gridRowName="1" gridColumnName={gridColumnName}>
				<LockingSystemItemWrapper isKey={true} isKeyFirstItem={true}>
					<div style={{ textAlign: "center", height: 130 }} id="edit-locking-system-key">
						<div>
							<button
								disabled={isFrozen ? false : isLoading}
								onClick={() => setEditComponentModalUuid(uuid)}
								style={{
									background: "rgba(70, 145, 206, 0.25)",
									width: 45,
									height: 40,
									borderRadius: 3,
									textAlign: "center",
									border: "none",
									outline: "none",
									cursor: "pointer",
								}}
							>
								<Icon
									path={isFrozen ? mdiInformation : mdiPencil}
									size={1}
									style={{ padding: 0 }}
									color="#4691CE"
								/>
							</button>
						</div>
						<Box style={{ marginTop: 2, marginBottom: 2 }}>
							<strong>{cellIndex + 1}</strong>
						</Box>
						<Box style={{ marginBottom: 3 }} id="locking-system-key-move">
							<IconButton
								disabled={isLoading}
								onClick={() => moveMatrixComponent(uuid, "down")}
								style={{ padding: 0 }}
								size="large"
							>
								<Icon path={mdiChevronLeft} size={0.75} />
							</IconButton>
							<IconButton
								disabled={isLoading}
								onClick={() => moveMatrixComponent(uuid, "up")}
								style={{ padding: 0 }}
								size="large"
							>
								<Icon path={mdiChevronRight} size={0.75} />
							</IconButton>
						</Box>
						<div style={{ display: "flex", alignItems: "center" }}>
							<IconButton
								disabled={isLoading || !duplicateKey}
								onClick={duplicate}
								style={{ padding: 0 }}
								size="large"
							>
								<Icon path={mdiContentCopy} size={0.9} />
							</IconButton>
							<IconButton
								disabled={isLoading || !removeKey}
								onClick={remove}
								style={{ padding: 0 }}
								size="large"
							>
								<Icon path={mdiTrashCanOutline} size={0.9} />
							</IconButton>
						</div>
					</div>
				</LockingSystemItemWrapper>
			</StyledLockingSystemCell>
			<StyledLockingSystemCell gridRowName="2" gridColumnName={gridColumnName}>
				<LockingSystemItemWrapper isKey={true} isKeyInput={true}>
					<div style={{ height: 201 }}>
						<TextInput
							name="key-name"
							label={`${fallbackName} (${formatPrice(
								totalGrossPriceCents + (charges?.totalGrossChargeCents ?? 0),
							)})`}
							value={name}
							onChange={onComponentNameChange}
							placeholder={fallbackName}
							disabled={isLoading || !setComponentName}
							style={{
								transform: "rotate(-90deg)",
								width: 160,
								marginLeft: -49,
								marginTop: 45,
							}}
							fullWidth
						/>
					</div>
				</LockingSystemItemWrapper>
			</StyledLockingSystemCell>
			<StyledLockingSystemCell gridRowName="3" gridColumnName={gridColumnName}>
				<LockingSystemItemWrapper
					isKey={true}
					isKeyLastItem={true}
					style={{
						background: "none",
						padding: 5,
						height: 41,
						boxShadow: "0 4px 2px -2px rgba(0,0,0,0.10)",
						borderBottomLeftRadius: 9,
						borderBottomRightRadius: 9,
					}}
				>
					<div
						style={{
							textAlign: "center",
							marginBottom: -5,
							marginTop: -15,
							marginLeft: 0,
						}}
					>
						<ChangeAmountButton
							quantity={quantity}
							onChange={onComponentQuantityChange}
							minQuantity={1}
							disabled={isLoading || !setComponentQuantity}
							isLockingSystemPage={true}
						/>
					</div>
					<div style={{ textAlign: "center" }}>
						<Tooltip title="Ganze Spalte auswählen">
							<IconButton
								style={{ padding: 0 }}
								onClick={onToggleSelections}
								disabled={isLoading || !toggleKeyComponentSelection}
								size="large"
							>
								<Icon
									path={mdiSortBoolAscendingVariant}
									size={0.9}
									color="#505E5F"
								/>
							</IconButton>
						</Tooltip>
					</div>
				</LockingSystemItemWrapper>
			</StyledLockingSystemCell>
		</>
	)
}

interface Props {
	isLoading: boolean
	isFrozen: boolean
	keyComponent: LockingSystemComponent
	charges: ComponentCharges
	cellIndex: number
	removeKey?: (uuid: string) => void
	duplicateKey?: (uuid: string) => void
	setComponentName?: (uuid: string, name: string) => void
	setComponentQuantity?: (uuid: string, quantity: number) => void
	setEditComponentModalUuid?: (uuid: string) => void
	moveMatrixComponent?: (uuid: string, direction: MoveDirection) => void
	toggleKeyComponentSelection?: (uuid: string) => void
}

export default LockingSystemKey
